import React, {useCallback} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { User, Lock } from "lucide-react";
import {useIntl} from "react-intl";
import {Link} from "react-router-dom";
import {useLocalStorage} from "react-use";

import s from "./style.module.css";
import {authStore} from "../../store/auth.store";
import {CONFIG_LS_KEY} from "../../constant/key";
import {AuthConfig} from "../../model/auth.model";
import {root} from "../../config/router/authorizedRouter";
import {restore} from "../../config/router/unauthorizedRouter";
import { Card, CardContent } from "../ui/card";
import { Input, PasswordInput } from "../ui/input";
import { Button } from "../ui/button";
import { Alert } from "../ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";

const LoginPage: React.FC = () => {
  const { formatMessage } = useIntl();

  const formSchema = z.object({
    username: z.string().min(1, { message: formatMessage({ id: 'ENTER_USERNAME' }) }),
    password: z.string().min(1, { message: formatMessage({ id: 'ENTER_PASSWORD' }) }),
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const [config, setConfig] = useLocalStorage<AuthConfig | null>(
    CONFIG_LS_KEY,
    null,
    { raw: true }
  );
  const logIn = authStore(state => state.logIn);
  const configError = authStore(state => state.error.config);
  const configLoading = authStore(state => state.loading.config);

  const setTokenCallback = useCallback((newConfig: AuthConfig) => {
    newConfig.token !== config?.token && setConfig(newConfig);
    window.location.pathname = root;
  }, [config?.token, setConfig]);

  const onSubmit = useCallback((values: FormValues) => {
    logIn(values.username, values.password, setTokenCallback);
  }, [logIn, setTokenCallback]);

  return (
    <Card className={s.LoginPage}>
      <CardContent className="pt-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder={formatMessage({ id: 'USER_NAME' })}
                      startIcon={<User />}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <PasswordInput
                      placeholder={formatMessage({ id: 'PASSWORD' })}
                      startIcon={<Lock />}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button variant="primary" type="submit" loading={configLoading} className="w-full">
              {formatMessage({ id: 'SIGN_IN' })}
            </Button>

            {configError && (
              <Alert variant="error">
                {configError.message}
              </Alert>
            )}

            <div className="flex justify-end">
              <Button variant="link" type="button" className="p-0">
                <Link to={restore}>{formatMessage({ id: 'FORGOT_PASSWORD' })}</Link>
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default LoginPage;
